/* eslint-disable max-lines */
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, EllipsisOutlined } from '@ant-design/icons'
import { useMutation, useSuspenseQuery } from '@apollo/client'
import { treeUtil } from '@salescore/buff-common'
import {
  type FetchMixedUsersQuery,
  FetchUserGroupAssignmentHistoriesDocument,
  FetchUserGroupRetainedDocument,
  type FetchUserGroupRetainedQuery,
  UpsertUserGroupAssignmentHistoriesDocument,
} from '@salescore/client-api'
import { getOrganizationIdFromPath } from '@salescore/client-base'
import { SuspenseWithLoading } from '@salescore/client-common'
import { stableStringify } from '@salescore/features'
// eslint-disable-next-line sonarjs/deprecation
import type { useModal } from '@salescore/frontend-common'
import { App, Button, DatePicker, Dropdown, Modal, Space, Table, TreeSelect } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { Array as A, Option, Order, pipe, Result } from 'effect'
import { t } from 'i18next'
import { useMemo, useState } from 'react'

type MixedUser = FetchMixedUsersQuery['mixedUsers'][0]

// --- 型定義 ---

export interface LocalHistory {
  id: string
  userGroupRetainedId: string | null
  userGroupId: string | null
  userGroupName: string | null
  validFrom: string
  validTo: string | null
  isNew: boolean
  isDeleted: boolean
  isPreviewing: boolean
  preEditSnapshot?: {
    userGroupRetainedId: string | null
    userGroupId: string | null
    userGroupName: string | null
    validFrom: string
    validTo: string | null
  }
  /** プレビュー開始時の上の行（新しい方）のID。disabledDate の制約に使用 */
  adjacentAboveId?: string
  /** プレビュー開始時の下の行（古い方）のID。disabledDate の制約に使用 */
  adjacentBelowId?: string
}

export interface ValidationError {
  type: 'overlap' | 'gap' | 'missingValidTo' | 'multipleCurrent' | 'zeroPeriod' | 'futureValidFromForCurrent'
  index: number
  message: string
}

// --- 純粋関数 ---

/** 最古行の validFrom に使用するセンチネル値（Unix epoch）。disabledDate の制約を実質無効化する */
const OLDEST_VALID_FROM = dayjs('1970-01-01').toISOString()

const validFromDescOrder = Order.mapInput(Order.flip(Order.String), (h: LocalHistory) => h.validFrom)

/** 最古行の validFrom を OLDEST_VALID_FROM に正規化する */
function normalizeOldestValidFrom(histories: LocalHistory[]): LocalHistory[] {
  const sorted = pipe(filterActive(histories), A.sortBy(validFromDescOrder))
  const oldestId = pipe(
    A.last(sorted),
    Option.map((h) => h.id),
    Option.getOrUndefined,
  )
  if (oldestId === undefined) {
    return histories
  }
  return pipe(
    histories,
    A.map((h) => (h.id === oldestId ? { ...h, validFrom: OLDEST_VALID_FROM } : h)),
  )
}

function filterActive(histories: LocalHistory[]): LocalHistory[] {
  return pipe(
    histories,
    A.filter((h) => !h.isDeleted),
  )
}

function filterValidatable(histories: LocalHistory[]): LocalHistory[] {
  return pipe(
    histories,
    A.filter((h) => !h.isDeleted && !(h.isPreviewing && h.isNew)),
  )
}

export function validateHistories(histories: LocalHistory[]): ValidationError[] {
  const active = filterValidatable(histories)

  // 現在の所属（validTo === null）が 2 件以上 → エラー
  const currentEntries = pipe(
    active,
    A.filter((h) => h.validTo === null),
  )
  const multipleCurrentErrors: ValidationError[] =
    currentEntries.length > 1
      ? pipe(
          currentEntries,
          A.map((entry) => ({
            type: 'multipleCurrent' as const,
            index: active.indexOf(entry),
            message: t('現在の所属が複数存在します'),
          })),
        )
      : []

  // validFrom 降順ソートして隣接チェック
  const sorted = pipe(active, A.sortBy(validFromDescOrder))

  // 連続性チェック: 隣接する行の validTo === validFrom でなければエラー（重複 or ギャップ）
  const continuityErrors: ValidationError[] =
    sorted.length >= 2
      ? pipe(
          A.zip(sorted, A.drop(sorted, 1)),
          A.filterMap(([current, next], index): Result.Result<ValidationError, void> => {
            if (next.validTo === null) {
              return Result.failVoid
            }
            if (current.validFrom === next.validTo) {
              return Result.failVoid
            }
            // next.validTo > current.validFrom → 古い行の終了が新しい行の開始より後 → 重複
            if (next.validTo > current.validFrom) {
              return Result.succeed({
                type: 'overlap' as const,
                index: index + 1,
                message: t('期間が重複しています'),
              })
            }
            return Result.succeed({
              type: 'gap' as const,
              index: index + 1,
              message: t('期間にギャップがあります'),
            })
          }),
        )
      : []

  // V4: 期間ゼロ・逆転チェック（validFrom >= validTo）
  const zeroPeriodErrors: ValidationError[] = pipe(
    sorted,
    A.filterMap((h, index) => {
      if (h.validTo === null) {
        return Result.failVoid
      }
      if (h.validFrom >= h.validTo) {
        return Result.succeed({
          type: 'zeroPeriod' as const,
          index,
          message: t('期間がゼロです'),
        })
      }
      return Result.failVoid
    }),
  )

  // 現在所属（validTo === null）の validFrom に未来日（明日以降）は不可
  const futureValidFromErrors: ValidationError[] = pipe(
    sorted,
    A.filterMap((h, index) => {
      if (h.validTo !== null) {
        return Result.failVoid
      }
      if (dayjs(h.validFrom).isAfter(dayjs(), 'day')) {
        return Result.succeed({
          type: 'futureValidFromForCurrent' as const,
          index,
          message: t('現在所属の開始日に未来日は指定できません'),
        })
      }
      return Result.failVoid
    }),
  )

  return [...multipleCurrentErrors, ...continuityErrors, ...zeroPeriodErrors, ...futureValidFromErrors]
}

export function toDisplayValidTo(validTo: string): string {
  return dayjs(validTo).subtract(1, 'day').toISOString()
}

export function fromDisplayValidTo(displayDate: Dayjs): string {
  return displayDate.add(1, 'day').toISOString()
}

export function isCellChanged(
  current: LocalHistory,
  initialHistories: LocalHistory[],
  field: 'userGroupRetainedId' | 'validFrom' | 'validTo',
): boolean {
  if (current.isNew) {
    return true
  }
  const initial = pipe(
    initialHistories,
    A.findFirst((h) => h.id === current.id),
    Option.getOrUndefined,
  )
  if (initial === undefined) {
    return true
  }
  const currentValue = current[field]
  const initialValue = initial[field]
  // null同士は変更なし
  if (currentValue === null && initialValue === null) {
    return false
  }
  return currentValue !== initialValue
}

interface TreeNode {
  title: string
  value: string
  disabled?: boolean
  children: TreeNode[]
}

export type RetainedGroup = FetchUserGroupRetainedQuery['userGroupRetained'][0]

const dIdKeys: Record<number, 'd1Id' | 'd2Id' | 'd3Id' | 'd4Id' | 'd5Id'> = {
  1: 'd1Id',
  2: 'd2Id',
  3: 'd3Id',
  4: 'd4Id',
  5: 'd5Id',
}

function getDId(r: RetainedGroup, depth: number): string | null | undefined {
  const key = dIdKeys[depth]
  if (key === undefined) return undefined
  return r[key]
}

function toRetainedTreeNodes(itemNodes: ReturnType<typeof treeUtil.createTreesFromItems<RetainedGroup>>): TreeNode[] {
  return pipe(
    itemNodes,
    // 子を持つノードを先、子を持たないノード（リーフ）を後に配置
    A.sortBy(Order.mapInput(Order.Boolean, (node) => node.children.length === 0)),
    A.map((node) => ({
      title: node.item.userGroupId === null ? `${node.item.name} (${t('削除済み')})` : node.item.name,
      value: node.item.userGroupRetainedId,
      children: toRetainedTreeNodes(node.children),
    })),
  )
}

export function buildRetainedTreeData(retainedGroups: RetainedGroup[], disabledRetainedIds: string[]): TreeNode[] {
  // d(depth)Id → userGroupRetainedId のマップ構築（null/undefined はスキップ）
  const originalIdToRetainedId = new Map(
    pipe(
      retainedGroups,
      A.filterMap((r) => {
        const selfDId = getDId(r, r.depth)
        if (selfDId === null || selfDId === undefined) {
          return Result.failVoid
        }
        return Result.succeed([selfDId, r.userGroupRetainedId] as const)
      }),
    ),
  )

  const tree = treeUtil.createTreesFromItems({
    items: retainedGroups,
    getId: (r) => r.userGroupRetainedId,
    getParentId: (r) => {
      const pid = getDId(r, r.depth - 1)
      if (pid === null || pid === undefined) {
        return
      }
      return originalIdToRetainedId.get(pid)
    },
  })
  const nodes = toRetainedTreeNodes(tree)
  const noTeamDisabled = disabledRetainedIds.includes('')
  const result: TreeNode[] = [{ title: t('所属なし'), value: '', disabled: noTeamDisabled, children: [] }, ...nodes]
  const nonEmptyIds = pipe(
    disabledRetainedIds,
    A.filter((id) => id !== ''),
  )
  if (A.isArrayNonEmpty(nonEmptyIds)) {
    return markDisabled(result, nonEmptyIds)
  }
  return result
}

function markDisabled(nodes: TreeNode[], disabledGroupIds: string[]): TreeNode[] {
  return pipe(
    nodes,
    A.map((node) => ({
      ...node,
      disabled: node.disabled === true || disabledGroupIds.includes(node.value),
      children: markDisabled(node.children, disabledGroupIds),
    })),
  )
}

/** プレビュー中の行で選べなくする、プレビュー開始時の上下の隣接行のグループ。所属なしは '' で表す */
export const findAdjacentRetainedIdsToDisable = (record: LocalHistory, histories: LocalHistory[]): string[] =>
  pipe(
    [record.adjacentAboveId, record.adjacentBelowId],
    A.filterMap((adjId) => {
      if (adjId === undefined) {
        return Result.failVoid
      }
      const adjRecord = pipe(
        histories,
        A.findFirst((h) => h.id === adjId),
        Option.getOrUndefined,
      )
      if (adjRecord?.userGroupRetainedId === undefined) {
        return Result.failVoid
      }
      return Result.succeed(adjRecord.userGroupRetainedId ?? '')
    }),
  )

export const hasSameGroupAsAdjacent = (
  current: LocalHistory,
  adjacentRecords: Array<LocalHistory | undefined>,
): boolean =>
  pipe(
    adjacentRecords,
    A.filterMap((adj) => {
      if (adj === undefined) {
        return Result.failVoid
      }
      return Result.succeed(adj.userGroupRetainedId)
    }),
  ).includes(current.userGroupRetainedId)

export function buildSavePayload(histories: LocalHistory[]): Array<{
  userGroupRetainedId: string | null
  validFrom: string
  validTo: string | null
}> {
  const active = pipe(filterValidatable(histories), A.sortBy(validFromDescOrder))
  return pipe(
    active,
    A.map((h, index) => ({
      userGroupRetainedId: h.userGroupRetainedId,
      // 最古の行（降順の末尾）の validFrom を OLDEST_VALID_FROM に固定
      validFrom: index === active.length - 1 ? OLDEST_VALID_FROM : h.validFrom,
      validTo: h.validTo,
    })),
  )
}

// --- コンポーネント ---

// eslint-disable-next-line sonarjs/deprecation
export const AssignmentHistoryModal = ({ modal }: { modal: ReturnType<typeof useModal<MixedUser>> }) => (
  <SuspenseWithLoading>
    <>{modal.content !== undefined && <Body user={modal.content} modal={modal} />}</>
  </SuspenseWithLoading>
)

// --- セルコンポーネント ---

// eslint-disable-next-line sonarjs/prefer-read-only-props
function GroupCell({
  record,
  histories,
  retainedGroups,
  onGroupChange,
}: {
  record: LocalHistory
  histories: LocalHistory[]
  retainedGroups: RetainedGroup[]
  onGroupChange: (record: LocalHistory, value: string) => void
}) {
  const isCurrent = record.validTo === null
  if (record.isPreviewing && !isCurrent) {
    const disabledRetainedIds = findAdjacentRetainedIdsToDisable(record, histories)
    const filteredTreeData = buildRetainedTreeData(retainedGroups, disabledRetainedIds)
    return (
      <TreeSelect
        style={{ width: '100%' }}
        value={record.userGroupRetainedId ?? ''}
        treeData={filteredTreeData}
        treeLine
        showSearch
        treeNodeFilterProp="title"
        treeDefaultExpandAll
        onChange={(value: string) => {
          onGroupChange(record, value)
        }}
      />
    )
  }
  return <span>{record.userGroupName ?? t('所属なし')}</span>
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function ValidFromCell({
  record,
  histories,
  isSingleRow,
  onValidFromChange,
}: {
  record: LocalHistory
  histories: LocalHistory[]
  isSingleRow: boolean
  onValidFromChange: (record: LocalHistory, date: Dayjs | null) => void
}) {
  const isOldest = record.validFrom === OLDEST_VALID_FROM
  if (isOldest) {
    return <span>-</span>
  }
  if (record.isPreviewing && !isSingleRow) {
    // 保存済みIDで隣接行を参照（日付編集で行のソート位置が変わってもずれない）
    const belowRecord =
      record.adjacentBelowId === undefined
        ? undefined
        : pipe(
            histories,
            A.findFirst((h) => h.id === record.adjacentBelowId),
            Option.getOrUndefined,
          )
    const aboveRecord =
      record.adjacentAboveId === undefined
        ? undefined
        : pipe(
            histories,
            A.findFirst((h) => h.id === record.adjacentAboveId),
            Option.getOrUndefined,
          )
    const disabledDate = (date: Dayjs) => {
      // 下の行の validFrom 以前 → 下の行が期間ゼロになる
      if (belowRecord !== undefined && !date.isAfter(dayjs(belowRecord.validFrom), 'day')) {
        return true
      }
      // 上の行の validFrom 以降 → 行の並び順が逆転する
      if (aboveRecord !== undefined && !date.isBefore(dayjs(aboveRecord.validFrom), 'day')) {
        return true
      }
      // 自身の validTo 以降 → 期間ゼロ/逆転
      if (record.validTo !== null && !date.isBefore(dayjs(record.validTo), 'day')) {
        return true
      }
      // 現在所属（validTo === null）の行は未来日（明日以降）を選択不可
      if (record.validTo === null && date.isAfter(dayjs(), 'day')) {
        return true
      }
      return false
    }
    return (
      <DatePicker
        value={dayjs(record.validFrom)}
        disabledDate={disabledDate}
        allowClear={false}
        onChange={(date: Dayjs | null) => {
          onValidFromChange(record, date)
        }}
        format="YYYY/MM/DD"
      />
    )
  }
  return <span>{dayjs(record.validFrom).format('YYYY/MM/DD')}</span>
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function ValidToCell({
  record,
  histories,
  isSingleRow,
  onValidToChange,
}: {
  record: LocalHistory
  histories: LocalHistory[]
  isSingleRow: boolean
  onValidToChange: (record: LocalHistory, date: Dayjs | null) => void
}) {
  if (record.validTo === null) {
    return <span>{t('現在')}</span>
  }
  if (record.isPreviewing && !isSingleRow) {
    // 保存済みIDで隣接行を参照
    const aboveRecord =
      record.adjacentAboveId === undefined
        ? undefined
        : pipe(
            histories,
            A.findFirst((h) => h.id === record.adjacentAboveId),
            Option.getOrUndefined,
          )
    const disabledDate = (date: Dayjs) => {
      // 表示日付 < validFrom → 期間ゼロ/逆転
      if (date.isBefore(dayjs(record.validFrom), 'day')) {
        return true
      }
      // 上の行が存在し validTo がある場合: 表示日付 >= aboveRow.validTo - 1日 → 上の行が期間ゼロ
      if (
        aboveRecord?.validTo !== null &&
        aboveRecord?.validTo !== undefined &&
        !date.isBefore(dayjs(aboveRecord.validTo).subtract(1, 'day'), 'day')
      ) {
        return true
      }
      return false
    }
    return (
      <DatePicker
        value={dayjs(toDisplayValidTo(record.validTo))}
        disabledDate={disabledDate}
        allowClear={false}
        onChange={(date: Dayjs | null) => {
          onValidToChange(record, date)
        }}
        format="YYYY/MM/DD"
      />
    )
  }
  return <span>{dayjs(toDisplayValidTo(record.validTo)).format('YYYY/MM/DD')}</span>
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function OperationsCell({
  record,
  isSingleRow,
  hasPreviewingRow,
  onConfirmPreview,
  onCancelPreview,
  onStartEditPreview,
  onDelete,
  onAddAbove,
  onAddBelow,
}: {
  record: LocalHistory
  isSingleRow: boolean
  hasPreviewingRow: boolean
  onConfirmPreview: (record: LocalHistory) => void
  onCancelPreview: (record: LocalHistory) => void
  onStartEditPreview: (record: LocalHistory) => void
  onDelete: (record: LocalHistory) => void
  onAddAbove: (record: LocalHistory) => void
  onAddBelow: (record: LocalHistory) => void
}) {
  // プレビュー行: ✓/✗ ボタン
  if (record.isPreviewing) {
    return (
      <Space size={4}>
        <Button
          icon={<CheckOutlined />}
          type="text"
          size="small"
          onClick={() => {
            onConfirmPreview(record)
          }}
        />
        <Button
          icon={<CloseOutlined />}
          type="text"
          size="small"
          danger
          onClick={() => {
            onCancelPreview(record)
          }}
        />
      </Space>
    )
  }

  const isCurrent = record.validTo === null
  const showEdit = !isSingleRow
  const showDelete = !isCurrent

  // プレビュー行が存在する間は他の行のアイコンを無効化
  if (hasPreviewingRow) {
    return (
      <Space size={4}>
        {showEdit && <Button icon={<EditOutlined />} type="text" size="small" disabled />}
        {showDelete && <Button icon={<DeleteOutlined />} type="text" size="small" disabled />}
        <Button icon={<EllipsisOutlined />} type="text" size="small" disabled />
      </Space>
    )
  }

  // 追加メニュー（⋮）
  const menuItems = [
    // 最新行（validTo=null）では「上に追加」非表示
    ...(isCurrent
      ? []
      : [
          {
            key: 'addAbove',
            label: t('上に追加'),
            onClick: () => {
              onAddAbove(record)
            },
          },
        ]),
    {
      key: 'addBelow',
      label: t('下に追加'),
      onClick: () => {
        onAddBelow(record)
      },
    },
  ]

  return (
    <Space size={4}>
      {showEdit && (
        <Button
          icon={<EditOutlined />}
          type="text"
          size="small"
          onClick={() => {
            onStartEditPreview(record)
          }}
        />
      )}
      {showDelete && (
        <Button
          icon={<DeleteOutlined />}
          type="text"
          size="small"
          danger
          onClick={() => {
            onDelete(record)
          }}
        />
      )}
      <Dropdown menu={{ items: menuItems }} trigger={['click']}>
        <Button icon={<EllipsisOutlined />} type="text" size="small" />
      </Dropdown>
    </Space>
  )
}

// eslint-disable-next-line sonarjs/deprecation,sonarjs/prefer-read-only-props
function Body({ user, modal }: { user: MixedUser; modal: ReturnType<typeof useModal<MixedUser>> }) {
  const organizationId = getOrganizationIdFromPath()
  const { message, modal: appModal } = App.useApp()

  // データ取得
  const { data: historiesData } = useSuspenseQuery(FetchUserGroupAssignmentHistoriesDocument, {
    variables: { organizationId, userId: user.id },
    fetchPolicy: 'network-only',
  })
  const { data: retainedData } = useSuspenseQuery(FetchUserGroupRetainedDocument, {
    variables: { organizationId },
    fetchPolicy: 'network-only',
  })
  const [upsertMutation, { loading: saving }] = useMutation(UpsertUserGroupAssignmentHistoriesDocument, {
    refetchQueries: ['fetchMixedUsers'],
    onCompleted: () => {
      void message.success(t('所属履歴を保存しました'))
      modal.hideModal()
    },
    onError: (error) => {
      void message.error(error.message)
    },
  })

  // サーバーデータの正規化（比較用・初期値用）
  const initialHistories = useMemo<LocalHistory[]>(() => {
    const mapped: LocalHistory[] = pipe(
      historiesData.userGroupAssignmentHistories,
      A.map((h) => ({
        id: h.id,
        userGroupRetainedId: h.userGroupRetainedId ?? null,
        userGroupId: h.userGroupId ?? null,
        userGroupName: h.userGroupName ?? null,
        validFrom: h.validFrom,
        validTo: h.validTo ?? null,
        isNew: false,
        isDeleted: false,
        isPreviewing: false,
      })),
    )
    // 降順ソート + 最古行の validFrom を Unix epoch に正規化（制約を実質無効化）
    const sorted = pipe(mapped, A.sortBy(validFromDescOrder))
    return normalizeOldestValidFrom(sorted)
  }, [historiesData])

  // ローカル状態
  const [histories, setHistories] = useState(initialHistories)

  // 表示用データを作成（プレビュー行がある間は挿入位置を維持するためソートしない）
  const hasPreviewingRow = pipe(
    histories,
    A.some((h) => h.isPreviewing),
  )
  const displayHistories = hasPreviewingRow
    ? filterActive(histories)
    : pipe(filterActive(histories), A.sortBy(validFromDescOrder))

  // バリデーション
  const validationErrors = validateHistories(histories)
  const hasErrors = validationErrors.length > 0

  // 未保存変更の検出
  const hasUnsavedChanges = stableStringify(histories) !== stableStringify(initialHistories)

  // 保存処理
  const handleSave = () => {
    if (hasErrors) {
      return
    }
    const payload = buildSavePayload(histories)
    void upsertMutation({
      variables: {
        organizationId,
        userId: user.id,
        histories: payload,
      },
    })
  }

  // キャンセル時の処理（未保存変更がある場合は確認ダイアログ）
  const handleClose = () => {
    if (hasUnsavedChanges) {
      appModal.confirm({
        title: t('確認'),
        content: t('未保存の変更があります。閉じてもよろしいですか？'),
        okText: t('閉じる'),
        cancelText: t('キャンセル'),
        onOk: () => {
          modal.hideModal()
        },
      })
    } else {
      modal.hideModal()
    }
  }

  // 行の追加（プレビュー状態で挿入、隣接行への連動なし）
  const handleAddBelow = (record: LocalHistory) => {
    setHistories((previous) => {
      const active = pipe(filterActive(previous), A.sortBy(validFromDescOrder))
      const recordIndex = active.findIndex((h) => h.id === record.id)
      // 下の行（古い方）を取得
      const belowRecord = pipe(A.get(active, recordIndex + 1), Option.getOrUndefined)
      // 新しい行の validTo = record の validFrom（上の行の開始日 = 新しい行の終了日）
      // 最古行の場合、record.validFrom は OLDEST_VALID_FROM（センチネル値）のため、record.validTo を基準に算出する
      const newValidTo =
        belowRecord === undefined
          ? // eslint-disable-next-line sonarjs/no-nested-conditional
            record.validTo === null
            ? dayjs().subtract(1, 'month').toISOString()
            : dayjs(record.validTo).subtract(1, 'month').toISOString()
          : record.validFrom
      // 新しい行の validFrom = 下の行の validTo。下の行がなければ新しい最古行になるので OLDEST_VALID_FROM を設定
      const newValidFrom = belowRecord?.validTo ?? OLDEST_VALID_FROM

      const newHistory: LocalHistory = {
        id: crypto.randomUUID(),
        userGroupRetainedId: null,
        userGroupId: null,
        userGroupName: null,
        validFrom: newValidFrom,
        validTo: newValidTo,
        isNew: true,
        isDeleted: false,
        isPreviewing: true,
        adjacentAboveId: record.id,
        adjacentBelowId: belowRecord?.id,
      }

      return pipe(
        previous,
        A.flatMap((h) => (h.id === record.id ? [h, newHistory] : [h])),
      )
    })
  }

  // 行の追加（上に追加: プレビュー状態で挿入、隣接行への連動なし）
  const handleAddAbove = (record: LocalHistory) => {
    setHistories((previous) => {
      const active = pipe(filterActive(previous), A.sortBy(validFromDescOrder))
      const recordIndex = active.findIndex((h) => h.id === record.id)
      // 上の行（新しい方）を取得
      const aboveRecord = pipe(A.get(active, recordIndex - 1), Option.getOrUndefined)
      // 新しい行の validFrom = record の validTo（下の行の終了日 = 新しい行の開始日）
      const newValidFrom = record.validTo ?? record.validFrom
      // 新しい行の validTo = 上の行の validFrom（上の行の開始日 = 新しい行の終了日）。上の行がなければ1ヶ月後を仮設定
      const newValidTo = aboveRecord?.validFrom ?? dayjs(newValidFrom).add(1, 'month').toISOString()

      const newHistory: LocalHistory = {
        id: crypto.randomUUID(),
        userGroupRetainedId: null,
        userGroupId: null,
        userGroupName: null,
        validFrom: newValidFrom,
        validTo: newValidTo,
        isNew: true,
        isDeleted: false,
        isPreviewing: true,
        adjacentAboveId: aboveRecord?.id,
        adjacentBelowId: record.id,
      }

      return pipe(
        previous,
        A.flatMap((h) => (h.id === record.id ? [newHistory, h] : [h])),
      )
    })
  }

  // プレビュー行の確定（✓ボタン）: 確定 → 双方向連動 → バリデーション
  const handleConfirmPreview = (record: LocalHistory) => {
    const currentRecord = pipe(
      histories,
      A.findFirst((h) => h.id === record.id),
      Option.getOrUndefined,
    )
    if (currentRecord === undefined) {
      return
    }

    // 確定して隣接行への連動を発火（保存済みの隣接行IDで参照）
    const confirmed = pipe(
      histories,
      A.map((h) =>
        h.id === record.id
          ? {
              ...h,
              isPreviewing: false,
              preEditSnapshot: undefined,
              adjacentAboveId: undefined,
              adjacentBelowId: undefined,
            }
          : h,
      ),
    )
    const belowRecord =
      currentRecord.adjacentBelowId === undefined
        ? undefined
        : pipe(
            confirmed,
            A.findFirst((h) => h.id === currentRecord.adjacentBelowId),
            Option.getOrUndefined,
          )
    const aboveRecord =
      currentRecord.adjacentAboveId === undefined
        ? undefined
        : pipe(
            confirmed,
            A.findFirst((h) => h.id === currentRecord.adjacentAboveId),
            Option.getOrUndefined,
          )

    // 隣接行と同じグループを選択していないかチェック
    const isDuplicateWithAdjacent = hasSameGroupAsAdjacent(currentRecord, [belowRecord, aboveRecord])
    if (isDuplicateWithAdjacent) {
      void message.error(t('隣接する履歴と同じグループは指定できません'))
      return
    }

    const linked = pipe(
      confirmed,
      A.map((h) => {
        // 前の行（古い方）の validTo を新しい行の validFrom に連動
        if (h.id === belowRecord?.id) {
          return { ...h, validTo: currentRecord.validFrom }
        }
        // 次の行（新しい方）の validFrom を新しい行の validTo に連動
        if (h.id === aboveRecord?.id && currentRecord.validTo !== null) {
          return { ...h, validFrom: currentRecord.validTo }
        }
        return h
      }),
    )

    // 連動適用後にバリデーション（連動前だと一時的な重複/ギャップで誤検出する）
    const errors = validateHistories(linked)
    if (errors.length > 0) {
      void message.error(
        pipe(
          errors,
          A.map((e) => e.message),
          A.dedupe,
          A.join('、'),
        ),
      )
      return
    }

    setHistories(linked)
  }

  // プレビュー行の取消（✗ボタン）: 新規行は削除、既存行は元の値に復元
  const handleCancelPreview = (record: LocalHistory) => {
    setHistories((previous) =>
      pipe(
        previous,
        A.map((h) => {
          if (h.id !== record.id) {
            return h
          }
          // 新規行: 削除
          if (h.isNew) {
            return { ...h, isDeleted: true, isPreviewing: false }
          }
          // 既存行: preEditSnapshot から復元
          if (h.preEditSnapshot !== undefined) {
            return {
              ...h,
              userGroupRetainedId: h.preEditSnapshot.userGroupRetainedId,
              userGroupId: h.preEditSnapshot.userGroupId,
              userGroupName: h.preEditSnapshot.userGroupName,
              validFrom: h.preEditSnapshot.validFrom,
              validTo: h.preEditSnapshot.validTo,
              isPreviewing: false,
              preEditSnapshot: undefined,
            }
          }
          return { ...h, isPreviewing: false }
        }),
      ),
    )
  }

  // 既存行の編集開始（✏️ボタン）: プレビュー状態にして元の値を保存
  const handleStartEditPreview = (record: LocalHistory) => {
    setHistories((previous) => {
      const active = pipe(filterActive(previous), A.sortBy(validFromDescOrder))
      const recordIndex = active.findIndex((h) => h.id === record.id)
      const aboveRecord = pipe(A.get(active, recordIndex - 1), Option.getOrUndefined)
      const belowRecord = pipe(A.get(active, recordIndex + 1), Option.getOrUndefined)

      return pipe(
        previous,
        A.map((h) =>
          h.id === record.id
            ? {
                ...h,
                isPreviewing: true,
                preEditSnapshot: {
                  userGroupRetainedId: h.userGroupRetainedId,
                  userGroupId: h.userGroupId,
                  userGroupName: h.userGroupName,
                  validFrom: h.validFrom,
                  validTo: h.validTo,
                },
                adjacentAboveId: aboveRecord?.id,
                adjacentBelowId: belowRecord?.id,
              }
            : h,
        ),
      )
    })
  }

  // 行の削除
  const handleDelete = (record: LocalHistory) => {
    setHistories((previous) => {
      // まず削除マークを付ける
      const updated = pipe(
        previous,
        A.map((h) => (h.id === record.id ? { ...h, isDeleted: true } : h)),
      )
      // 削除後のactive行をソートして隣接行を特定
      const active = pipe(filterActive(updated), A.sortBy(validFromDescOrder))
      // 削除前のactive行で削除行の位置を特定
      const previousActive = pipe(filterActive(previous), A.sortBy(validFromDescOrder))
      const deletedIndex = previousActive.findIndex((h) => h.id === record.id)
      // 削除行の上の行（新しい方）と下の行（古い方）を繋ぐ
      const aboveRecord = pipe(A.get(previousActive, deletedIndex - 1), Option.getOrUndefined)
      const belowRecord = pipe(A.get(previousActive, deletedIndex + 1), Option.getOrUndefined)

      if (aboveRecord !== undefined && belowRecord !== undefined) {
        // 中間行を削除: 下の行の validTo = 削除行の validTo（下の行が前進して隙間を埋める）
        return pipe(
          updated,
          A.map((h) => (h.id === belowRecord.id ? { ...h, validTo: record.validTo } : h)),
        )
      }
      if (aboveRecord !== undefined && belowRecord === undefined) {
        // 最古の行を削除: 上の行が新しい最古行になる。validFrom を OLDEST_VALID_FROM に正規化
        return pipe(
          updated,
          A.map((h) => (h.id === aboveRecord.id ? { ...h, validFrom: OLDEST_VALID_FROM } : h)),
        )
      }
      // 最新行の削除は isCurrent=true の場合は起きないが、念のため
      return pipe(
        active,
        A.head,
        Option.match({
          onNone: () => updated,
          onSome: () =>
            belowRecord === undefined
              ? updated
              : pipe(
                  updated,
                  A.map((h) => (h.id === belowRecord.id ? { ...h, validTo: record.validTo } : h)),
                ),
        }),
      )
    })
  }

  // グループ変更
  const handleGroupChange = (record: LocalHistory, value: string) => {
    const retainedRecord = pipe(
      retainedData.userGroupRetained,
      A.findFirst((r) => r.userGroupRetainedId === value),
      Option.getOrUndefined,
    )

    setHistories((previous) =>
      pipe(
        previous,
        A.map((h) =>
          h.id === record.id
            ? {
                ...h,
                userGroupRetainedId: value === '' ? null : value,
                userGroupId: value === '' ? null : (retainedRecord?.userGroupId ?? null),
                userGroupName: value === '' ? null : (retainedRecord?.name ?? null),
              }
            : h,
        ),
      ),
    )
  }

  // 開始日変更（双方向連動: 前の行の終了日を連動更新。プレビュー行の場合は連動スキップ）
  const handleValidFromChange = (record: LocalHistory, date: Dayjs | null) => {
    if (date === null) {
      return
    }
    const newValidFrom = date.toISOString()
    setHistories((previous) => {
      // プレビュー行は自身の値のみ更新（隣接行への連動は確定時に発火）
      if (record.isPreviewing) {
        return pipe(
          previous,
          A.map((h) => (h.id === record.id ? { ...h, validFrom: newValidFrom } : h)),
        )
      }
      const active = pipe(filterActive(previous), A.sortBy(validFromDescOrder))
      const recordIndex = active.findIndex((h) => h.id === record.id)
      // 前の行（古い方、降順で1つ下）
      const belowRecord = pipe(A.get(active, recordIndex + 1), Option.getOrUndefined)

      return pipe(
        previous,
        A.map((h) => {
          if (h.id === record.id) {
            return { ...h, validFrom: newValidFrom }
          }
          // 前の行（古い方）の validTo を連動更新
          if (h.id === belowRecord?.id) {
            return { ...h, validTo: newValidFrom }
          }
          return h
        }),
      )
    })
  }

  // 終了日変更（双方向連動: 次の行の開始日を連動更新、A2: fromDisplayValidTo変換。プレビュー行の場合は連動スキップ）
  const handleValidToChange = (record: LocalHistory, date: Dayjs | null) => {
    const validTo = date === null ? null : fromDisplayValidTo(date)
    setHistories((previous) => {
      // プレビュー行は自身の値のみ更新（隣接行への連動は確定時に発火）
      if (record.isPreviewing) {
        return pipe(
          previous,
          A.map((h) => (h.id === record.id ? { ...h, validTo } : h)),
        )
      }
      const active = pipe(filterActive(previous), A.sortBy(validFromDescOrder))
      const recordIndex = active.findIndex((h) => h.id === record.id)
      // 次の行（新しい方、降順で1つ上）
      const aboveRecord = pipe(A.get(active, recordIndex - 1), Option.getOrUndefined)

      return pipe(
        previous,
        A.map((h) => {
          if (h.id === record.id) {
            return { ...h, validTo }
          }
          // 次の行（新しい方）の validFrom を連動更新
          if (h.id === aboveRecord?.id && validTo !== null) {
            return { ...h, validFrom: validTo }
          }
          return h
        }),
      )
    })
  }

  // テーブルカラム定義
  const isSingleRow = displayHistories.length === 1
  const columns = [
    {
      title: t('グループ名'),
      dataIndex: 'userGroupId',
      width: 200,
      onCell(record: LocalHistory) {
        const changed = isCellChanged(record, initialHistories, 'userGroupRetainedId')
        return changed ? { style: { backgroundColor: '#e6f7ff' } } : {}
      },
      render(_: unknown, record: LocalHistory) {
        return (
          <GroupCell
            record={record}
            histories={histories}
            retainedGroups={retainedData.userGroupRetained}
            onGroupChange={handleGroupChange}
          />
        )
      },
    },
    {
      title: t('開始日'),
      dataIndex: 'validFrom',
      width: 150,
      onCell(record: LocalHistory) {
        const changed = isCellChanged(record, initialHistories, 'validFrom')
        return changed ? { style: { backgroundColor: '#e6f7ff' } } : {}
      },
      render(_: unknown, record: LocalHistory) {
        return (
          <ValidFromCell
            record={record}
            histories={histories}
            isSingleRow={isSingleRow}
            onValidFromChange={handleValidFromChange}
          />
        )
      },
    },
    {
      title: t('終了日'),
      dataIndex: 'validTo',
      width: 150,
      onCell(record: LocalHistory) {
        const changed = isCellChanged(record, initialHistories, 'validTo')
        return changed ? { style: { backgroundColor: '#e6f7ff' } } : {}
      },
      render(_: unknown, record: LocalHistory) {
        return (
          <ValidToCell
            record={record}
            histories={histories}
            isSingleRow={isSingleRow}
            onValidToChange={handleValidToChange}
          />
        )
      },
    },
    {
      title: t('操作'),
      width: 120,
      render(_: unknown, record: LocalHistory) {
        return (
          <OperationsCell
            record={record}
            isSingleRow={isSingleRow}
            hasPreviewingRow={hasPreviewingRow}
            onConfirmPreview={handleConfirmPreview}
            onCancelPreview={handleCancelPreview}
            onStartEditPreview={handleStartEditPreview}
            onDelete={handleDelete}
            onAddAbove={handleAddAbove}
            onAddBelow={handleAddBelow}
          />
        )
      },
    },
  ]

  return (
    <Modal
      open={modal.isModalVisible}
      onCancel={handleClose}
      width={'60%'}
      footer={null}
      destroyOnHidden
      title={t('{{name}} の所属履歴', { name: user.resourceUser.name })}
    >
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={displayHistories}
        columns={columns}
        onRow={(record: LocalHistory) => ({
          style: record.isPreviewing ? { backgroundColor: '#e6f7ff' } : undefined,
        })}
      />
      {hasErrors && !hasPreviewingRow && (
        <div style={{ marginTop: 8 }}>
          {pipe(
            validationErrors,
            A.map((error) => (
              <div key={`${error.type}-${String(error.index)}`} style={{ color: 'red' }}>
                {error.message}
              </div>
            )),
          )}
        </div>
      )}
      <Space style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={handleClose}>{t('キャンセル')}</Button>
        <Button
          type="primary"
          disabled={hasErrors || !hasUnsavedChanges || hasPreviewingRow}
          loading={saving}
          onClick={handleSave}
        >
          {t('保存')}
        </Button>
      </Space>
    </Modal>
  )
}
